import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  ArrowLeft, Save, Plus, Trash2, GripVertical, RefreshCw, FileText, Eye,
} from 'lucide-react';
import useSensitiveDataAck from '../hooks/useSensitiveDataAck';
import SensitiveDataDisclaimerModal from '../components/SensitiveDataDisclaimerModal';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Default settings shape — must match the PHP-side default in
 * api/utils/invoicePdf.php > invoiceMergeSettings(). The editor only
 * displays controls for keys that exist in this object.
 */
const DEFAULT_SETTINGS = {
  show_logo: true,
  logo_url: '',
  show_business_header: true,
  business_name_override: '',
  business_address_override: '',
  business_tax_id: '',
  show_tax_breakdown: true,
  show_payment_method: true,
  show_signature_line: false,
  show_provider_block: false,
  show_provider_license: false,
  show_service_codes: false,
  show_diagnostic_code: false,
  // Insurance-ready flags (migration 036).
  show_insurance_block: false,
  show_paid_date: false,
  auto_receipt_when_paid: false,
  tagline_text: '',
  tagline_position: 'header',
  thank_you_text: 'Thank you for your business!',
  show_terms: false,
  terms_text: '',
  accent_color: '#0ea5e9',
  heading_font: 'Helvetica',
  body_font: 'Helvetica',
  document_title: 'Invoice',
  document_number_prefix: 'INV-',
};

const FONT_OPTIONS = ['Helvetica', 'Arial', 'Times', 'Courier'];
const FIELD_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Yes / No' },
];
const ASK_AT_OPTIONS = [
  { value: 'invoice_only', label: 'On the invoice itself (fillable)' },
  { value: 'booking', label: 'During booking' },
  { value: 'payment', label: 'At payment' },
];

const InvoiceTemplateEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [name, setName] = useState('');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewBust, setPreviewBust] = useState(0);
  const previewIframeRef = useRef(null);

  // Sensitive-data disclaimer gate. Invoice templates can carry health
  // information (diagnostic codes, license numbers, custom intake answers),
  // so the first save attempt by an unacknowledged admin pops the modal.
  const ack = useSensitiveDataAck();
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/invoice-templates/${id}`, authHeader());
      setTemplate(res.data);
      setName(res.data.name || '');
      setSettings({ ...DEFAULT_SETTINGS, ...(res.data.settings || {}) });
      setQuestions(res.data.questions || []);
    } catch (e) {
      toast.error('Failed to load template');
      navigate('/invoice-templates');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  // Internal save: skips the disclaimer gate. Called directly by the public
  // handleSave (when ack already on file) or by the disclaimer modal after
  // the admin confirms.
  const performSave = async () => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/invoice-templates/${id}`,
        { name: name.trim(), settings },
        authHeader()
      );
      toast.success('Template saved');
      // Refresh preview iframe.
      setPreviewBust((n) => n + 1);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    // First-time gate: invoice templates with provider info / diagnostic
    // codes / custom health questions are explicitly listed as a sensitive
    // feature in the disclaimer. Block until acknowledged.
    if (!ack.loading && !ack.acknowledged) {
      setShowDisclaimer(true);
      return;
    }
    await performSave();
  };

  const refreshPreview = async () => {
    // Save first so preview reflects the latest settings.
    await handleSave();
    setPreviewBust((n) => n + 1);
  };

  // ─── Question handlers ────────────────────────────────────────────────
  const addQuestion = async () => {
    const label = window.prompt(
      'Question label (e.g. "Where did you hear about us?")',
      ''
    );
    if (!label || !label.trim()) return;
    try {
      const res = await axios.post(
        `${API_URL}/invoice-templates/${id}/questions`,
        {
          label: label.trim(),
          field_type: 'text',
          ask_at: 'invoice_only',
          required: false,
          display_order: questions.length,
          is_active: true,
        },
        authHeader()
      );
      setQuestions((prev) => [...prev, res.data]);
      toast.success('Question added');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to add question');
    }
  };

  const updateQuestion = async (qid, patch) => {
    const current = questions.find((q) => q.id === qid);
    if (!current) return;
    const next = { ...current, ...patch };
    setQuestions((prev) => prev.map((q) => (q.id === qid ? next : q)));
    try {
      await axios.put(
        `${API_URL}/invoice-templates/${id}/questions/${qid}`,
        {
          label: next.label,
          field_type: next.field_type,
          options: next.options,
          required: !!next.required,
          ask_at: next.ask_at,
          display_order: next.display_order,
          is_active: next.is_active !== false,
        },
        authHeader()
      );
    } catch (e) {
      toast.error('Failed to update question');
      load(); // re-sync
    }
  };

  const deleteQuestion = async (qid) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`${API_URL}/invoice-templates/${id}/questions/${qid}`, authHeader());
      setQuestions((prev) => prev.filter((q) => q.id !== qid));
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const moveQuestion = async (qid, delta) => {
    const idx = questions.findIndex((q) => q.id === qid);
    if (idx < 0) return;
    const target = idx + delta;
    if (target < 0 || target >= questions.length) return;
    const reordered = [...questions];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setQuestions(reordered);
    try {
      await axios.post(
        `${API_URL}/invoice-templates/${id}/questions/reorder`,
        { order: reordered.map((q) => q.id) },
        authHeader()
      );
    } catch (e) {
      toast.error('Failed to reorder');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      {showDisclaimer && (
        <SensitiveDataDisclaimerModal
          featureName="insurance-ready invoice templates"
          onAcknowledge={async () => {
            await ack.acknowledge();
            // Continue the save the admin originally clicked.
            await performSave();
          }}
          onClose={() => setShowDisclaimer(false)}
        />
      )}
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sticky top-0 bg-gray-50 py-3 z-10">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/invoice-templates')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              title="Back to templates"
            >
              <ArrowLeft size={24} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <FileText size={24} className="text-primary-600" />
                <span>Edit invoice template</span>
              </h1>
              <p className="text-gray-500 text-sm">
                Changes apply to new PDFs generated from this template.
                {template?.is_default && ' This is the active default template.'}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={refreshPreview}
              className="btn btn-outline flex items-center space-x-2"
              disabled={saving}
            >
              <RefreshCw size={16} />
              <span>Save & refresh preview</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Save size={16} />
              <span>{saving ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT: configuration panes */}
          <div className="space-y-6">
            {/* Identity */}
            <Section title="Template identity">
              <Field label="Template name" hint="For your reference only — not shown on invoices.">
                <input
                  type="text"
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                />
              </Field>
              <Field label="Document title" hint='Shows in big text at the top right ("Invoice", "Receipt", "Quote", etc.).'>
                <input
                  type="text"
                  className="input"
                  value={settings.document_title}
                  onChange={(e) => updateSetting('document_title', e.target.value)}
                  maxLength={40}
                />
              </Field>
              <Field label="Document number prefix" hint='Each generated invoice gets a number like "INV-AB12CD34". Change the prefix here.'>
                <input
                  type="text"
                  className="input"
                  value={settings.document_number_prefix}
                  onChange={(e) => updateSetting('document_number_prefix', e.target.value)}
                  maxLength={10}
                />
              </Field>
            </Section>

            {/* Branding */}
            <Section title="Branding">
              <Toggle
                checked={settings.show_logo}
                onChange={(v) => updateSetting('show_logo', v)}
                label="Show logo"
              />
              {settings.show_logo && (
                <Field label="Logo URL (override)" hint="Leave blank to use the business logo from Business Info.">
                  <input
                    type="url"
                    className="input"
                    value={settings.logo_url || ''}
                    onChange={(e) => updateSetting('logo_url', e.target.value)}
                    placeholder="https://..."
                  />
                </Field>
              )}
              <Field label="Accent color" hint="Used for headings, totals, and the divider line.">
                <input
                  type="color"
                  value={settings.accent_color}
                  onChange={(e) => updateSetting('accent_color', e.target.value)}
                  className="w-20 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <span className="ml-3 text-sm text-gray-600 font-mono">{settings.accent_color}</span>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Heading font">
                  <select
                    className="input"
                    value={settings.heading_font}
                    onChange={(e) => updateSetting('heading_font', e.target.value)}
                  >
                    {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </Field>
                <Field label="Body font">
                  <select
                    className="input"
                    value={settings.body_font}
                    onChange={(e) => updateSetting('body_font', e.target.value)}
                  >
                    {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </Field>
              </div>
            </Section>

            {/* Business header */}
            <Section title="Business header">
              <Toggle
                checked={settings.show_business_header}
                onChange={(v) => updateSetting('show_business_header', v)}
                label="Show business name + address block"
              />
              {settings.show_business_header && (
                <>
                  <Field label="Business name override" hint="Leave blank to use the name from Business Info.">
                    <input
                      type="text"
                      className="input"
                      value={settings.business_name_override || ''}
                      onChange={(e) => updateSetting('business_name_override', e.target.value)}
                      placeholder="(uses Business Info)"
                    />
                  </Field>
                  <Field label="Address override (multi-line)" hint="Leave blank to auto-build from Business Info address fields.">
                    <textarea
                      className="input"
                      rows={3}
                      value={settings.business_address_override || ''}
                      onChange={(e) => updateSetting('business_address_override', e.target.value)}
                      placeholder="(uses Business Info)"
                    />
                  </Field>
                  <Field label="Tax ID / Business number" hint="Required on Canadian receipts so customers can submit them to insurers.">
                    <input
                      type="text"
                      className="input"
                      value={settings.business_tax_id || ''}
                      onChange={(e) => updateSetting('business_tax_id', e.target.value)}
                      placeholder="123456789RT0001"
                    />
                  </Field>
                </>
              )}
            </Section>

            {/* Tagline */}
            <Section title="Tagline / message">
              <Field label="Tagline text" hint='Optional one-liner. Example: "Serving the Bay Area since 2014."'>
                <input
                  type="text"
                  className="input"
                  value={settings.tagline_text || ''}
                  onChange={(e) => updateSetting('tagline_text', e.target.value)}
                  maxLength={200}
                />
              </Field>
              {settings.tagline_text && (
                <Field label="Tagline position">
                  <div className="flex space-x-3">
                    <Radio
                      checked={settings.tagline_position === 'header'}
                      onChange={() => updateSetting('tagline_position', 'header')}
                      label="Header (under business info)"
                    />
                    <Radio
                      checked={settings.tagline_position === 'footer'}
                      onChange={() => updateSetting('tagline_position', 'footer')}
                      label="Footer (above terms)"
                    />
                  </div>
                </Field>
              )}
              <Field label="Thank-you message" hint="Displayed in your accent color near the bottom. Leave blank to omit.">
                <input
                  type="text"
                  className="input"
                  value={settings.thank_you_text || ''}
                  onChange={(e) => updateSetting('thank_you_text', e.target.value)}
                  maxLength={200}
                />
              </Field>
            </Section>

            {/* Provider info */}
            <Section
              title="Provider info"
              description="Adds the assigned employee's name and (optionally) their license/registration number. Required on insurance-submittable receipts for regulated professions."
            >
              <Toggle
                checked={settings.show_provider_block}
                onChange={(v) => updateSetting('show_provider_block', v)}
                label="Show service provider name"
              />
              {settings.show_provider_block && (
                <Toggle
                  checked={settings.show_provider_license}
                  onChange={(v) => updateSetting('show_provider_license', v)}
                  label="Show provider license # and association"
                  hint="Pulls from each user's profile (License number / Professional association fields)."
                />
              )}
            </Section>

            {/* Codes */}
            <Section
              title="Service codes"
              description="Print the service code (CPT / provincial / internal SKU) and an optional per-appointment diagnostic code on the line item. Useful for insurance receipts."
            >
              <Toggle
                checked={settings.show_service_codes}
                onChange={(v) => updateSetting('show_service_codes', v)}
                label="Show service code column"
                hint="Set per-service codes in the Services Manager."
              />
              <Toggle
                checked={settings.show_diagnostic_code}
                onChange={(v) => updateSetting('show_diagnostic_code', v)}
                label="Show diagnostic code (per-appointment)"
                hint="Filled in by admin/staff when generating each invoice — e.g. ICD-10 code."
              />
            </Section>

            {/* Insurance / claim */}
            <Section
              title="Insurance / claim"
              description="For clients who need to submit the receipt to their own insurer. Adds insurer / policy / group / claim-reference fields and lets the document be rendered as a paid Receipt rather than an Invoice."
            >
              <Toggle
                checked={settings.show_insurance_block}
                onChange={(v) => updateSetting('show_insurance_block', v)}
                label="Show insurance / claim block"
                hint="Per-appointment fields (insurer, policy #, group #, claim ref, notes) entered in the Invoice data modal."
              />
              <Toggle
                checked={settings.show_paid_date}
                onChange={(v) => updateSetting('show_paid_date', v)}
                label="Show paid date alongside service date"
                hint="Some insurers require both — the date of service and the date payment was received."
              />
              <Toggle
                checked={settings.auto_receipt_when_paid}
                onChange={(v) => updateSetting('auto_receipt_when_paid', v)}
                label='Auto-title "Receipt" when fully paid'
                hint='When the appointment payment_status is "paid", the document title automatically becomes "Receipt". Insurers prefer Receipt on a paid document since "Invoice" implies a balance owing. Per-appointment receipt toggle in the Invoice data modal also forces this on.'
              />
            </Section>

            {/* Payment + tax */}
            <Section title="Payment & tax">
              <Toggle
                checked={settings.show_tax_breakdown}
                onChange={(v) => updateSetting('show_tax_breakdown', v)}
                label="Show subtotal + tax breakdown"
              />
              <Toggle
                checked={settings.show_payment_method}
                onChange={(v) => updateSetting('show_payment_method', v)}
                label="Show payment method + status"
              />
              <Toggle
                checked={settings.show_signature_line}
                onChange={(v) => updateSetting('show_signature_line', v)}
                label="Show client signature line"
              />
            </Section>

            {/* Terms */}
            <Section title="Terms & conditions">
              <Toggle
                checked={settings.show_terms}
                onChange={(v) => updateSetting('show_terms', v)}
                label="Show terms block at bottom"
              />
              {settings.show_terms && (
                <Field label="Terms text">
                  <textarea
                    className="input font-mono text-xs"
                    rows={4}
                    value={settings.terms_text || ''}
                    onChange={(e) => updateSetting('terms_text', e.target.value)}
                    placeholder="All sales final. Refunds available within 7 days with original receipt..."
                  />
                </Field>
              )}
            </Section>

            {/* Custom questions */}
            <Section
              title="Custom client questions"
              description="Stripe-style questions you can attach to bookings, payments, or the invoice itself. Each answer is stored against the appointment and printed on the PDF."
            >
              {questions.length === 0 ? (
                <div className="text-sm text-gray-500 italic mb-3">
                  No questions yet. Add one to start collecting custom info.
                </div>
              ) : (
                <div className="space-y-3 mb-3">
                  {questions.map((q, idx) => (
                    <QuestionRow
                      key={q.id}
                      question={q}
                      onChange={(patch) => updateQuestion(q.id, patch)}
                      onDelete={() => deleteQuestion(q.id)}
                      onMoveUp={idx > 0 ? () => moveQuestion(q.id, -1) : null}
                      onMoveDown={idx < questions.length - 1 ? () => moveQuestion(q.id, 1) : null}
                    />
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={addQuestion}
                className="btn btn-outline inline-flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Add question</span>
              </button>
            </Section>
          </div>

          {/* RIGHT: live preview */}
          <div className="lg:sticky lg:top-24 self-start">
            <div className="card p-0 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                  <Eye size={16} />
                  <span>Live preview (sample data)</span>
                </div>
                <button
                  onClick={refreshPreview}
                  className="text-xs text-primary-600 hover:underline inline-flex items-center space-x-1"
                  title="Save and refresh"
                >
                  <RefreshCw size={12} />
                  <span>Refresh</span>
                </button>
              </div>
              <PreviewFrame
                templateId={id}
                cacheBust={previewBust}
                ref={previewIframeRef}
              />
              <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">
                Click "Save & refresh preview" after toggling settings. The preview
                uses synthetic sample appointment data, not real customer info.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components ────────────────────────────────────────────────────

const Section = ({ title, description, children }) => (
  <div className="card">
    <h2 className="text-lg font-bold text-gray-900 mb-1">{title}</h2>
    {description && <p className="text-sm text-gray-500 mb-3">{description}</p>}
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const Toggle = ({ checked, onChange, label, hint }) => (
  <div>
    <label className="flex items-start space-x-3 cursor-pointer">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 text-primary-600 rounded"
      />
      <span>
        <span className="text-sm font-medium text-gray-800">{label}</span>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </span>
    </label>
  </div>
);

const Radio = ({ checked, onChange, label }) => (
  <label className="flex items-center space-x-2 cursor-pointer text-sm">
    <input
      type="radio"
      checked={checked}
      onChange={onChange}
      className="h-4 w-4 text-primary-600"
    />
    <span>{label}</span>
  </label>
);

const QuestionRow = ({ question, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const [optionsText, setOptionsText] = useState((question.options || []).join('\n'));

  // Push options array up to parent when textarea blurs.
  const commitOptions = () => {
    const arr = optionsText.split('\n').map((s) => s.trim()).filter(Boolean);
    onChange({ options: arr });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <div className="flex items-start space-x-2">
        <div className="flex flex-col space-y-1 pt-1">
          {onMoveUp && (
            <button
              onClick={onMoveUp}
              className="text-gray-400 hover:text-gray-700 text-xs"
              title="Move up"
            >▲</button>
          )}
          {onMoveDown && (
            <button
              onClick={onMoveDown}
              className="text-gray-400 hover:text-gray-700 text-xs"
              title="Move down"
            >▼</button>
          )}
          <GripVertical size={14} className="text-gray-300" />
        </div>
        <div className="flex-1 space-y-2">
          <input
            type="text"
            className="input"
            value={question.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="Question label"
            maxLength={255}
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              className="input"
              value={question.field_type}
              onChange={(e) => onChange({ field_type: e.target.value })}
            >
              {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select
              className="input"
              value={question.ask_at}
              onChange={(e) => onChange({ ask_at: e.target.value })}
            >
              {ASK_AT_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {question.field_type === 'select' && (
            <textarea
              className="input text-xs font-mono"
              rows={3}
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              onBlur={commitOptions}
              placeholder="One option per line"
            />
          )}
          <div className="flex items-center space-x-4 text-xs">
            <label className="flex items-center space-x-1 cursor-pointer">
              <input
                type="checkbox"
                checked={!!question.required}
                onChange={(e) => onChange({ required: e.target.checked })}
                className="h-3 w-3"
              />
              <span>Required</span>
            </label>
            <label className="flex items-center space-x-1 cursor-pointer">
              <input
                type="checkbox"
                checked={question.is_active !== false}
                onChange={(e) => onChange({ is_active: e.target.checked })}
                className="h-3 w-3"
              />
              <span>Active</span>
            </label>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="p-1 text-red-500 hover:bg-red-50 rounded"
          title="Delete question"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

/**
 * PreviewFrame — fetches the rendered HTML with the auth header (the
 * preview endpoint is admin-gated) and shoves it into an iframe via
 * srcdoc. Re-fetches whenever cacheBust changes.
 */
const PreviewFrame = React.forwardRef(({ templateId, cacheBust }, ref) => {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let abort = false;
    setLoading(true);
    fetch(`${API_URL}/invoice-templates/${templateId}/preview`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
    })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error('preview failed'))))
      .then((text) => { if (!abort) setHtml(text); })
      .catch(() => { if (!abort) setHtml('<p style="padding:32px;color:#a00;">Failed to load preview.</p>'); })
      .finally(() => { if (!abort) setLoading(false); });
    return () => { abort = true; };
  }, [templateId, cacheBust]);

  return (
    <div className="bg-gray-100" style={{ height: '720px' }}>
      {loading && (
        <div className="flex items-center justify-center h-full text-gray-500 text-sm">Loading preview…</div>
      )}
      {!loading && (
        <iframe
          ref={ref}
          title="Invoice preview"
          srcDoc={html}
          className="w-full h-full bg-white"
          sandbox="allow-same-origin"
        />
      )}
    </div>
  );
});

export default InvoiceTemplateEditor;
